import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/hooks/global-tools';
import useDeactivatedApprovers from '@/hooks/approval/useDeactivatedApprovers';
import ChevronLeftIcon from '@/public/icons/approval/chevron-left.svg';
import ChevronRightIcon from '@/public/icons/approval/chevron-right.svg';
import RecentApprovalLineCard from './RecentApprovalLineCard';
import RecentApprovalLineSkeleton from './RecentApprovalLineSkeleton';
import {
  HistoryListContent,
  HistoryListPagination,
  HistoryListPaginationButton,
  HistoryListPaginationCurrent,
  HistoryListPaginationDivider,
  HistoryListPaginationLabel,
  HistoryListPaginationNumber,
} from './styles';

export type RecentApprovalLineApprover = {
  passportId: string;
  name: string;
  positionName: string;
  departmentName: string;
  profileImageUrl: string | null;
  /** 퇴사 여부 — 표시 뱃지에 퇴사 라벨을 붙일 때 사용. 스키마 외 확장 필드 */
  deactivated?: boolean;
};

export type RecentApprovalLineSummary = {
  id: string;
  title: string;
  approvers: RecentApprovalLineApprover[];
  /** 협조결재 멤버. 데이터 존재 시 결재라인 row에 협조 placeholder chip이 끼고, 협조결재 영역이 노출됨. */
  cooperators?: RecentApprovalLineApprover[];
  /** 협조결재 단계가 끼는 위치 (approvers 내 0..length). undefined면 끝 */
  cooperationInsertIndex?: number;
  /** 협조결재 순번 처리 여부 — true면 멤버 사이 chevron으로 순서 노출. undefined=false */
  cooperationOrdered?: boolean;
  /** 참조인 — 디테일 popup/drawer에서만 노출 */
  referrers?: RecentApprovalLineApprover[];
  lastUsedAt: string;
};

type RecentApprovalLineListProps = {
  items: RecentApprovalLineSummary[];
  /** 모바일 케밥 클릭 — bottom drawer open */
  onCardKebabClick?: (item: RecentApprovalLineSummary) => void;
  /** 카드 본문(전체 영역) 클릭 — 결재라인 적용 */
  onCardSelect?: (item: RecentApprovalLineSummary) => void;
  /** 모바일에서 케밥 노출 여부 */
  showKebab?: boolean;
  /** 카드 풋터 날짜 노출 여부 — '나의 결재라인'은 미노출 */
  showDate?: boolean;
  /** 현재 popover/drawer로 펼쳐진 항목 id (있으면 그 카드는 active로 표시) */
  activeItemId?: string | null;
  /** 페이지 전환/재조회 대기 — 직전 페이지 카드 대신 스켈레톤을 같은 수만큼 표시 */
  loading?: boolean;
  /** 서버사이드 페이지네이션 — 1-based 현재 페이지 */
  currentPage: number;
  /** 서버사이드 페이지네이션 — 마지막 페이지 번호 */
  totalPages: number;
  /** 페이지 변경 — 부모가 새 페이지의 items를 fetch */
  onPageChange: (page: number) => void;
};

type CardEntryProps = {
  item: RecentApprovalLineSummary;
  active: boolean;
  showKebab: boolean;
  showDate: boolean;
  onKebabClick?: (item: RecentApprovalLineSummary) => void;
  onCardSelect?: (item: RecentApprovalLineSummary) => void;
};

/** 퇴사자 passportId 집합을 결재자/협조자/참조인에 표시 플래그로 머지 */
export const withDeactivatedFlags = (
  item: RecentApprovalLineSummary,
  deactivatedIds: Set<string>,
): RecentApprovalLineSummary => {
  if (deactivatedIds.size === 0) return item;
  const mark = (a: RecentApprovalLineApprover): RecentApprovalLineApprover =>
    deactivatedIds.has(a.passportId) ? { ...a, deactivated: true } : a;
  return {
    ...item,
    approvers: item.approvers.map(mark),
    cooperators: item.cooperators?.map(mark),
    referrers: item.referrers?.map(mark),
  };
};

/**
 * 카드별 퇴사자 조회 결과를 item에 머지해 전달.
 * 콜백도 머지된 item을 넘겨야 케밥 드로어에 퇴사 라벨이 유지됨.
 */
const RecentApprovalLineCardWithDeactivation = ({
  item,
  onKebabClick,
  onCardSelect,
  ...rest
}: CardEntryProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { deactivatedIds, isLoading: isDeactivationLoading } =
    useDeactivatedApprovers(item.id);
  const enriched = useMemo(
    () => withDeactivatedFlags(item, deactivatedIds),
    [item, deactivatedIds],
  );
  return (
    <RecentApprovalLineCard
      item={enriched}
      onKebabClick={onKebabClick ? () => onKebabClick(enriched) : undefined}
      onCardClick={
        onCardSelect
          ? () => {
              // 퇴사자 조회 응답 전에 적용하면 제외·상신 차단이 모두 빠져나감 — 로딩 중엔 적용 대신 안내
              if (isDeactivationLoading) {
                snackbar.show({
                  message: t(
                    '결재라인 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.',
                  ),
                  severity: 'info',
                });
                return;
              }
              onCardSelect(enriched);
            }
          : undefined
      }
      {...rest}
    />
  );
};

const RecentApprovalLineList = ({
  items,
  onCardKebabClick,
  onCardSelect,
  showKebab = false,
  showDate = true,
  activeItemId = null,
  loading = false,
  currentPage,
  totalPages,
  onPageChange,
}: RecentApprovalLineListProps) => {
  const { t } = useTranslation();
  return (
    <HistoryListContent>
      {loading ? (
        <RecentApprovalLineSkeleton count={items.length} />
      ) : (
        items.map((item) => (
          <RecentApprovalLineCardWithDeactivation
            key={item.id}
            item={item}
            active={activeItemId === item.id}
            showKebab={showKebab}
            showDate={showDate}
            onKebabClick={onCardKebabClick}
            onCardSelect={onCardSelect}
          />
        ))
      )}
      {totalPages > 1 && (
        <HistoryListPagination>
          <HistoryListPaginationButton
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            aria-label={t('이전 페이지')}
          >
            <ChevronLeftIcon width={10} height={10} />
          </HistoryListPaginationButton>
          <HistoryListPaginationLabel>
            <HistoryListPaginationCurrent>
              {currentPage}
            </HistoryListPaginationCurrent>
            <HistoryListPaginationDivider>/</HistoryListPaginationDivider>
            <HistoryListPaginationNumber>
              {totalPages}
            </HistoryListPaginationNumber>
          </HistoryListPaginationLabel>
          <HistoryListPaginationButton
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            aria-label={t('다음 페이지')}
          >
            <ChevronRightIcon width={10} height={10} />
          </HistoryListPaginationButton>
        </HistoryListPagination>
      )}
    </HistoryListContent>
  );
};

export default RecentApprovalLineList;
